import { Link } from "react-router-dom";
import { cases, CATEGORIES, type CaseItem } from "@/mocks/fancha";

const catLabel = (key: string) => CATEGORIES.find((c) => c.key === key)?.label ?? "综合";

interface CasesSectionProps {
  cat: string;
  q: string;
  onCatChange: (key: string) => void;
}

function FeaturedCard({ item }: { item: CaseItem }) {
  return (
    <Link
      to={`/topic/${item.id}`}
      className="group grid grid-cols-1 overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-shadow hover:shadow-lift md:grid-cols-2"
    >
      <div className="relative aspect-[16/10] overflow-hidden md:aspect-auto md:min-h-[320px]">
        <img
          src={item.cover}
          alt={`${item.person}：${item.title}`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <span className="absolute left-4 top-4 inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
          <i className="ri-flag-fill" /> 本周头号专题
        </span>
      </div>
      <div className="flex flex-col justify-center p-6 md:p-10">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span className="font-semibold text-primary-600">{catLabel(item.category)}</span>
          <span aria-hidden="true">·</span>
          <span>{item.published}</span>
          <span className="ml-auto flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-accent-800">
            <i className="ri-fire-fill" /> 热度 {item.hot}
          </span>
        </div>
        <h3 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
          {item.title}
        </h3>
        <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">{item.paragraphs[0]}</p>
        <div className="mt-6 flex items-center gap-4 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground-950 text-background-50">
              <i className="ri-user-3-line text-xs" />
            </span>
            <span className="whitespace-nowrap font-medium text-foreground-700">{item.person}</span>
          </span>
          <span className="whitespace-nowrap">{item.views} 浏览</span>
          <span className="whitespace-nowrap">{item.comments} 评论</span>
        </div>
        <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary-600">
          进入完整深扒档案
          <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

function CaseCard({ item }: { item: CaseItem }) {
  return (
    <Link
      to={`/topic/${item.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 transition-shadow hover:shadow-lift"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={item.cover}
          alt={`${item.person}：${item.title}`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
        />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-foreground-950/85 px-2.5 py-1 text-xs font-semibold text-background-50">
          <i className="ri-focus-3-line" /> {item.tag}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span className="font-semibold text-primary-600">{catLabel(item.category)}</span>
          <span aria-hidden="true">·</span>
          <span>{item.published}</span>
          <span className="ml-auto flex items-center gap-0.5 text-primary-500">
            <i className="ri-fire-fill" /> {item.hot}
          </span>
        </div>
        <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950">
          {item.title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-6 text-foreground-600">{item.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-4 text-xs text-foreground-500">
          <span className="whitespace-nowrap font-medium text-foreground-700">{item.person}</span>
          <span className="whitespace-nowrap">
            {item.views} 浏览 · {item.comments} 评论
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function CasesSection({ cat, q, onCatChange }: CasesSectionProps) {
  const keyword = q.trim().toLowerCase();
  const filtered = cases.filter((c) => {
    const matchCat = cat === "all" || c.category === cat;
    const matchQ =
      !keyword ||
      [c.title, c.summary, c.person, c.tag, ...c.watch].join(" ").toLowerCase().includes(keyword);
    return matchCat && matchQ;
  });

  const [featuredItem, ...rest] = filtered;

  return (
    <section id="cases" className="scroll-mt-20 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">ARCHIVE</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              反转档案
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              把每个人设的「光鲜面」与「另一面」放在一起，深扒来龙去脉、时间线与当事人前后对比。
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1 rounded-full bg-background-100 p-1">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => onCatChange(c.key)}
                className={`whitespace-nowrap cursor-pointer rounded-full px-4 py-1.5 text-sm transition-colors ${
                  cat === c.key
                    ? "bg-foreground-950 font-semibold text-background-50"
                    : "text-foreground-600 hover:bg-background-200 hover:text-foreground-900"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-400" />
            <p className="mt-3 font-heading text-lg font-bold text-foreground-800">没有找到匹配的反转档案</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或分类试试看。</p>
            <button
              type="button"
              onClick={() => onCatChange("all")}
              className="mt-4 cursor-pointer rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-700 hover:bg-background-100"
            >
              查看全部档案
            </button>
          </div>
        ) : (
          <>
            {featuredItem && <div className="mt-8"><FeaturedCard item={featuredItem} /></div>}
            {rest.length > 0 && (
              <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((item) => (
                  <CaseCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}